export type PublicSource = {
  title: string;
  publisher: string | null;
  url: string;
  date: string | null;
  relation: "original" | "later_addition";
  addedAt: string | null;
};

function host(u: string) {
  try { return new URL(u).host.replace(/^www\./, ""); } catch { return ""; }
}

function fmtDay(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : null;
}

/** Splits approved sources: originals were used to write the piece; later additions appeared after it. */
export function splitSources(sources: PublicSource[]) {
  return {
    original: sources.filter((s) => s.relation === "original"),
    later: sources.filter((s) => s.relation === "later_addition"),
  };
}

/** Only original sources become `citation`. Later additions are deliberately NOT in structured data (reader-only). */
export function sourceStructuredData(sources: PublicSource[]) {
  const { original } = splitSources(sources);
  if (!original.length) return {};
  return {
    citation: original.map((s) => ({
      "@type": "CreativeWork",
      name: s.title,
      url: s.url,
      ...(s.publisher ? { publisher: { "@type": "Organization", name: s.publisher } } : {}),
      ...(s.date ? { datePublished: s.date } : {}),
    })),
  };
}

function List({ items, later }: { items: PublicSource[]; later?: boolean }) {
  return (
    <ol className="mt-4 list-decimal space-y-3 pl-5 font-serif text-[15.5px] leading-relaxed text-ink">
      {items.map((s) => (
        <li key={s.url}>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-ink/70">
            {s.title}
            <span className="sr-only"> (yeni sekmede açılır)</span>
          </a>
          <span className="block text-sm text-ink/65">
            {[s.publisher, s.date, host(s.url), later && s.addedAt ? `Eklenme: ${fmtDay(s.addedAt)}` : null].filter(Boolean).join(" · ")}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Editor-approved sources at the end of an article. Renders nothing when empty. */
export function ArticleSources({ sources }: { sources: PublicSource[] }) {
  const { original, later } = splitSources(sources);
  if (!original.length && !later.length) return null;
  return (
    <>
      {original.length > 0 && (
        <section aria-labelledby="kaynaklar" className="mt-12 border-t border-ink pt-5">
          <h2 id="kaynaklar" className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-ink">Kaynaklar</h2>
          <List items={original} />
        </section>
      )}
      {later.length > 0 && (
        <section aria-labelledby="sonradan-eklenen" className="mt-10 border-t border-dashed border-ink/40 pt-5">
          <h2 id="sonradan-eklenen" className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-ink/80">Sonradan eklenen ilgili kaynaklar</h2>
          <p className="mt-2 text-sm text-ink/65">Bu kaynaklar yazı yayımlandıktan sonra ortaya çıktı; yazının hazırlanmasında kullanılmadı. Konuyu güncel bilgiyle izlemek için eklendi.</p>
          <List items={later} later />
        </section>
      )}
    </>
  );
}
